import MenuIcon from '@material-symbols/svg-300/rounded/menu.svg'
import React, { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  userCanAddUserToGroupAsGroupManager,
  userCanAddUserToGroupAsMember,
  userCanRemoveUserFromGroupAsGroupManager,
  userCanRemoveUserFromGroupAsMember,
} from '../../state/calculations/permissions/userCan'
import useAddGroupManagersToGroups from '../../state/mutations/useAddGroupManagersToGroups'
import useAddMembersToGroups from '../../state/mutations/useAddMembersToGroups'
import { useGroup } from '../../state/entities'
import useRemoveGroupManagersFromGroups from '../../state/mutations/useRemoveGroupManagersFromGroups'
import useRemoveMembersFromGroups from '../../state/mutations/useRemoveMembersFromGroups'
import { useUser } from '../../state/entities'
import { useSession } from '../../state/session'
import useDialog from '../../utilities/useDialog'
import Button from '../basics/Button/Button'
import ConfirmDialog from '../basics/ConfirmDialog/ConfirmDialog'
import Menu from '../basics/Menu/Menu'
import GroupSlug from '../slugs/GroupSlug/GroupSlug'
import UserSlug from '../slugs/UserSlug/UserSlug'

interface GroupOperationDialogProps {
  userUuid: string
  groupUuid: string
  close: (result: boolean) => void
}

/**********************************************************************
 * ConfirmRemoveMemberFromGroupDialog
 **********************************************************************/
const ConfirmRemoveMemberFromGroupDialog = React.memo(function ConfirmRemoveMemberFromGroupDialog({
  userUuid,
  groupUuid,
  close,
}: GroupOperationDialogProps) {
  const { t } = useTranslation()

  const { user } = useUser(userUuid)
  const { group } = useGroup(groupUuid)

  const isGroupManager = useMemo(() => user?.managedGroups?.some(({ uuid }) => uuid === groupUuid), [user, groupUuid])

  return (
    <ConfirmDialog close={close} yesLabel={t('generic cta.remove')} noLabel={t('generic cta.cancel')}>
      <p>
        <Trans t={t} i18nKey="manage group.should user be removed as member from group">
          <UserSlug user={user} />
          <GroupSlug group={group} />
        </Trans>
      </p>
      {isGroupManager && <p>{t('manage group.the user will still be group manager of this group')}</p>}
    </ConfirmDialog>
  )
})

/**********************************************************************
 * ConfirmRemoveGroupManagerFromGroupDialog
 **********************************************************************/
const ConfirmRemoveGroupManagerFromGroupDialog = React.memo(function ConfirmRemoveGroupManagerFromGroupDialog({
  userUuid,
  groupUuid,
  close,
}: GroupOperationDialogProps) {
  const { t } = useTranslation()

  const { user } = useUser(userUuid)
  const { group } = useGroup(groupUuid)

  const isMember = useMemo(() => user?.groups?.some(({ uuid }) => uuid === groupUuid), [user, groupUuid])

  return (
    <ConfirmDialog close={close} yesLabel={t('generic cta.remove')} noLabel={t('generic cta.cancel')}>
      <p>
        <Trans t={t} i18nKey="manage group.should user be removed as group manager from group">
          <UserSlug user={user} />
          <GroupSlug group={group} />
        </Trans>
      </p>
      {isMember && <p>{t('manage group.the user will still be member of this group')}</p>}
    </ConfirmDialog>
  )
})

/**********************************************************************
 * GroupUserOperations
 **********************************************************************/
export default React.memo(function GroupUserOperations({
  groupUuid,
  userUuid,
}: {
  groupUuid: string
  userUuid: string
}) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()

  const { user } = useUser(userUuid)
  const { group } = useGroup(groupUuid)

  const isMember = useMemo(() => user?.groups?.some(({ uuid }) => uuid === groupUuid), [user, groupUuid])
  const isGroupManager = useMemo(() => user?.managedGroups?.some(({ uuid }) => uuid === groupUuid), [user, groupUuid])

  const showDialog = useDialog()

  // actions
  const actionItems: React.ReactNode[] = []

  // remove as group manager
  const removeGroupManagersFromGroup = useRemoveGroupManagersFromGroups()
  const handleRemoveGroupManager = useCallback(async () => {
    const confirmed = await showDialog(
      (close) => <ConfirmRemoveGroupManagerFromGroupDialog close={close} userUuid={userUuid} groupUuid={groupUuid} />,
      () => false
    )
    if (confirmed) {
      removeGroupManagersFromGroup.mutate({ groupUuids: [groupUuid], userUuids: [userUuid] })
    }
  }, [showDialog, userUuid, groupUuid, removeGroupManagersFromGroup])
  if (isGroupManager && userCanRemoveUserFromGroupAsGroupManager(currentUser, user, group)) {
    actionItems.push(
      <li key="remove-as-group-manager">
        <Button onClick={handleRemoveGroupManager} menuItem>
          {t('manage group.remove as group manager')}
        </Button>
      </li>
    )
  }

  // add as group manager
  const addGroupManagersToGroups = useAddGroupManagersToGroups()
  const handleAddGroupManager = useCallback(
    () => addGroupManagersToGroups.mutate({ groupUuids: [groupUuid], userUuids: [userUuid] }),
    [userUuid, groupUuid, addGroupManagersToGroups]
  )
  if (!isGroupManager && userCanAddUserToGroupAsGroupManager(currentUser, user, group)) {
    actionItems.push(
      <li key="add-as-group-manager">
        <Button onClick={handleAddGroupManager} menuItem>
          {t('manage group.add as group manager')}
        </Button>
      </li>
    )
  }

  // remove as member
  const removeMembersFromGroup = useRemoveMembersFromGroups()
  const handleRemoveMember = useCallback(async () => {
    const confirmed = await showDialog(
      (close) => <ConfirmRemoveMemberFromGroupDialog close={close} userUuid={userUuid} groupUuid={groupUuid} />,
      () => false
    )
    if (confirmed) {
      removeMembersFromGroup.mutate({ groupUuids: [groupUuid], userUuids: [userUuid] })
    }
  }, [showDialog, userUuid, groupUuid, removeMembersFromGroup])
  if (isMember && userCanRemoveUserFromGroupAsMember(currentUser, user, group)) {
    actionItems.push(
      <li key="remove-as-member">
        <Button onClick={handleRemoveMember} menuItem>
          {t('manage group.remove as group member')}
        </Button>
      </li>
    )
  }

  // add as member
  const addMembersToGroups = useAddMembersToGroups()
  const handleAddMember = useCallback(
    () => addMembersToGroups.mutate({ groupUuids: [groupUuid], userUuids: [userUuid] }),
    [userUuid, groupUuid, addMembersToGroups]
  )
  if (!isMember && userCanAddUserToGroupAsMember(currentUser, user, group)) {
    actionItems.push(
      <li key="add-as-member">
        <Button onClick={handleAddMember} menuItem>
          {t('manage group.add as group member')}
        </Button>
      </li>
    )
  }

  if (!actionItems.length) {
    return null
  }

  return (
    <Menu small label={<MenuIcon />}>
      {actionItems}
    </Menu>
  )
})
